import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import WizardYesNoChoice from "./WizardYesNoChoice";
import InstagramIcon from "@/components/icons/InstagramIcon";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import EmailIcon from "@/components/icons/EmailIcon";
import { settingsAddModalMaxWidthClassName } from "@/lib/settings-modals";
import { CHANNEL_CONNECT_HELP_LINKS } from "@/lib/channel-connect-help-links";
import { apiClient } from "@/lib/api-client";
import { startEmailConnect } from "@/lib/connections-status-api";
import { cn } from "@/lib/utils";
import type {
  ChannelConnectType,
  EmailProviderChoice,
  InstagramAnswers,
  InstagramWizardStep,
  StudioOption,
  WhatsAppAnswers,
  WhatsAppWizardStep,
  EmailWizardStep,
} from "./types";

const IG_STEPS: InstagramWizardStep[] = [
  "intro",
  "q_professional",
  "q_facebook_page",
  "q_message_access",
  "confirm_oauth",
];

const WA_STEPS: WhatsAppWizardStep[] = ["intro", "q_business_account", "q_meta_access", "confirm_oauth"];

const EMAIL_STEPS: EmailWizardStep[] = ["intro", "provider", "prereqs", "confirm_oauth"];

export type ChannelConnectWizardProps = {
  channel: ChannelConnectType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  studioId: string;
  returnPath: string;
  onSuccess?: () => void;
  /** Email default-studio picker (multi-studio tenants). */
  studios?: StudioOption[];
};

function HelpLink({ href, label }: { href: string; label: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">
      {label}
    </a>
  );
}

export default function ChannelConnectWizard({
  channel,
  open,
  onOpenChange,
  studioId,
  returnPath,
  onSuccess,
  studios = [],
}: ChannelConnectWizardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isInstagram = channel === "instagram";
  const isEmail = channel === "email";
  const steps = isInstagram ? IG_STEPS : isEmail ? EMAIL_STEPS : WA_STEPS;

  const [stepIndex, setStepIndex] = useState(0);
  const [igAnswers, setIgAnswers] = useState<InstagramAnswers>({
    professional: null,
    facebookPage: null,
    messageAccess: null,
  });
  const [waAnswers, setWaAnswers] = useState<WhatsAppAnswers>({
    businessAccount: null,
    metaAccess: null,
  });
  const [emailProvider, setEmailProvider] = useState<EmailProviderChoice>(null);
  const [emailDefaultStudioId, setEmailDefaultStudioId] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState(false);

  const resolvedEmailDefaultStudioId = useMemo(() => {
    if (emailDefaultStudioId) return emailDefaultStudioId;
    if (studios.length === 1) return studios[0]!.id;
    return studioId;
  }, [emailDefaultStudioId, studios, studioId]);

  const resetWizard = useCallback(() => {
    setStepIndex(0);
    setIgAnswers({ professional: null, facebookPage: null, messageAccess: null });
    setWaAnswers({ businessAccount: null, metaAccess: null });
    setEmailProvider(null);
    setEmailDefaultStudioId(studios.length === 1 ? studios[0]!.id : studioId);
    setConnecting(false);
    setConnectError(false);
  }, [studioId, studios]);

  useEffect(() => {
    if (!open) resetWizard();
  }, [open, resetWizard]);

  const currentStep = steps[stepIndex] ?? steps[0];

  const canContinue = (): boolean => {
    if (currentStep === "intro") return true;
    if (isEmail) {
      if (currentStep === "provider") return emailProvider !== null;
      if (currentStep === "prereqs") return true;
      return false;
    }
    if (isInstagram) {
      if (currentStep === "q_professional") return igAnswers.professional === true;
      if (currentStep === "q_facebook_page") return igAnswers.facebookPage === true;
      if (currentStep === "q_message_access") return igAnswers.messageAccess === true;
    } else {
      if (currentStep === "q_business_account") return waAnswers.businessAccount === true;
      if (currentStep === "q_meta_access") return waAnswers.metaAccess === true;
    }
    return false;
  };

  const goNext = () => {
    if (stepIndex < steps.length - 1) setStepIndex((i) => i + 1);
  };

  const goBack = () => {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
  };

  const handleConnect = async () => {
    setConnectError(false);
    setConnecting(true);
    try {
      if (isEmail) {
        const res = await startEmailConnect({
          returnPath,
          defaultStudioId: resolvedEmailDefaultStudioId || undefined,
        });
        if (res.success && res.data?.authorizeUrl) {
          window.location.assign(res.data.authorizeUrl);
          return;
        }
        setConnectError(true);
        return;
      }

      if (isInstagram) {
        const res = await apiClient<{ redirectUrl?: string }>("/connections/instagram/reconnect", {
          method: "POST",
          idempotent: true,
          studioId,
          body: JSON.stringify({ returnPath }),
        });
        if (res.success && res.data?.redirectUrl) {
          window.location.assign(res.data.redirectUrl);
          return;
        }
        setConnectError(true);
      } else {
        const res = await apiClient<{
          connected?: boolean;
          displayPhone?: string;
          redirectUrl?: string;
          startUrl?: string;
        }>("/connections/whatsapp/reconnect", {
          method: "POST",
          idempotent: true,
          studioId,
          body: JSON.stringify({ returnPath }),
        });
        if (!res.success) {
          setConnectError(true);
          return;
        }
        const data = res.data;
        if (data?.connected) {
          onOpenChange(false);
          onSuccess?.();
          return;
        }
        const redirectTarget = data?.startUrl ?? data?.redirectUrl;
        if (redirectTarget) {
          const signupParams = new URLSearchParams({
            return_to: returnPath,
            studio_id: studioId,
          });
          onOpenChange(false);
          navigate(`/whatsapp-signup?${signupParams.toString()}`);
          return;
        }
        setConnectError(true);
      }
    } catch {
      setConnectError(true);
    } finally {
      setConnecting(false);
    }
  };

  const title = isInstagram
    ? t("channelConnect.wizard.instagram.title")
    : isEmail
      ? t("channelConnect.email.wizard.title")
      : t("channelConnect.wizard.whatsapp.title");

  const ChannelIcon = isInstagram ? InstagramIcon : isEmail ? EmailIcon : WhatsAppIcon;
  const iconWrap = isInstagram
    ? "bg-[hsl(330_50%_92%)] text-[hsl(330_50%_35%)]"
    : isEmail
      ? "bg-[hsl(210_50%_92%)] text-[hsl(210_50%_35%)]"
      : "bg-[hsl(160_50%_90%)] text-[hsl(160_50%_30%)]";

  const renderBody = () => {
    if (currentStep === "intro") {
      return (
        <DialogDescription className="text-sm text-muted-foreground">
          {isInstagram
            ? t("channelConnect.wizard.instagram.introBody")
            : isEmail
              ? t("channelConnect.email.wizard.introBody")
              : t("channelConnect.wizard.whatsapp.introBody")}
        </DialogDescription>
      );
    }

    if (isEmail && currentStep === "provider") {
      return (
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t("channelConnect.email.wizard.providerGoogle")}</p>
          <WizardYesNoChoice
            value={emailProvider === "google" ? true : emailProvider === "microsoft" ? false : null}
            onChange={(v) => setEmailProvider(v === true ? "google" : v === false ? "microsoft" : null)}
            yesLabel={t("channelConnect.email.wizard.providerGoogle")}
            noLabel={t("channelConnect.email.wizard.providerMicrosoft")}
            groupLabel={t("channelConnect.email.wizard.title")}
          />
        </div>
      );
    }

    if (isEmail && currentStep === "prereqs") {
      return (
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">{t("channelConnect.email.wizard.prereqsTitle")}</p>
          <DialogDescription className="text-sm text-muted-foreground">
            {t("channelConnect.email.wizard.prereqsBody")}
          </DialogDescription>
        </div>
      );
    }

    if (isInstagram && currentStep === "q_professional") {
      return (
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t("channelConnect.wizard.instagram.q1")}</p>
          <WizardYesNoChoice
            value={igAnswers.professional}
            onChange={(v) => setIgAnswers((a) => ({ ...a, professional: v }))}
            yesLabel={t("channelConnect.wizard.instagram.q1Yes")}
            noLabel={t("channelConnect.wizard.instagram.q1No")}
            groupLabel={t("channelConnect.wizard.instagram.q1")}
          />
          {igAnswers.professional === false && (
            <Collapsible defaultOpen className="rounded-xl border border-border/50 bg-muted/50 p-3 text-sm">
              <CollapsibleTrigger className="font-medium text-foreground">
                {t("channelConnect.wizard.instagram.q1HelpTitle")}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2 text-muted-foreground">
                <p>{t("channelConnect.wizard.instagram.q1Help")}</p>
                <HelpLink href={CHANNEL_CONNECT_HELP_LINKS.instagramProfessional} label={t("channelConnect.wizard.learnMore")} />
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      );
    }

    if (isInstagram && currentStep === "q_facebook_page") {
      return (
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t("channelConnect.wizard.instagram.q2")}</p>
          <WizardYesNoChoice
            value={igAnswers.facebookPage}
            onChange={(v) => setIgAnswers((a) => ({ ...a, facebookPage: v }))}
            yesLabel={t("channelConnect.wizard.instagram.q2Yes")}
            noLabel={t("channelConnect.wizard.instagram.q2No")}
            groupLabel={t("channelConnect.wizard.instagram.q2")}
          />
          {igAnswers.facebookPage === false && (
            <Collapsible defaultOpen className="rounded-xl border border-border/50 bg-muted/50 p-3 text-sm">
              <CollapsibleTrigger className="font-medium text-foreground">
                {t("channelConnect.wizard.instagram.q2HelpTitle")}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2 text-muted-foreground">
                <p>{t("channelConnect.wizard.instagram.q2Help")}</p>
                <HelpLink href={CHANNEL_CONNECT_HELP_LINKS.instagramFacebookPage} label={t("channelConnect.wizard.learnMore")} />
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      );
    }

    if (isInstagram && currentStep === "q_message_access") {
      return (
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t("channelConnect.wizard.instagram.q3")}</p>
          <WizardYesNoChoice
            value={igAnswers.messageAccess}
            onChange={(v) => setIgAnswers((a) => ({ ...a, messageAccess: v }))}
            yesLabel={t("channelConnect.wizard.instagram.q3Yes")}
            noLabel={t("channelConnect.wizard.instagram.q3No")}
            groupLabel={t("channelConnect.wizard.instagram.q3")}
          />
          {igAnswers.messageAccess === false && (
            <Collapsible defaultOpen className="rounded-xl border border-border/50 bg-muted/50 p-3 text-sm">
              <CollapsibleTrigger className="font-medium text-foreground">
                {t("channelConnect.wizard.instagram.q3HelpTitle")}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2 text-muted-foreground">
                <p>{t("channelConnect.wizard.instagram.q3Help")}</p>
                <HelpLink
                  href={CHANNEL_CONNECT_HELP_LINKS.instagramMessageAccess}
                  label={t("channelConnect.wizard.learnMore")}
                />
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      );
    }

    if (!isInstagram && !isEmail && currentStep === "q_business_account") {
      return (
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t("channelConnect.wizard.whatsapp.q1")}</p>
          <WizardYesNoChoice
            value={waAnswers.businessAccount}
            onChange={(v) => setWaAnswers((a) => ({ ...a, businessAccount: v }))}
            yesLabel={t("channelConnect.wizard.whatsapp.q1Yes")}
            noLabel={t("channelConnect.wizard.whatsapp.q1No")}
            groupLabel={t("channelConnect.wizard.whatsapp.q1")}
          />
          {waAnswers.businessAccount === false && (
            <Collapsible defaultOpen className="rounded-xl border border-border/50 bg-muted/50 p-3 text-sm">
              <CollapsibleTrigger className="font-medium text-foreground">
                {t("channelConnect.wizard.whatsapp.q1HelpTitle")}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2 text-muted-foreground">
                <p>{t("channelConnect.wizard.whatsapp.q1Help")}</p>
                <HelpLink href={CHANNEL_CONNECT_HELP_LINKS.whatsappBusiness} label={t("channelConnect.wizard.learnMore")} />
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      );
    }

    if (!isInstagram && !isEmail && currentStep === "q_meta_access") {
      return (
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t("channelConnect.wizard.whatsapp.q2")}</p>
          <WizardYesNoChoice
            value={waAnswers.metaAccess}
            onChange={(v) => setWaAnswers((a) => ({ ...a, metaAccess: v }))}
            yesLabel={t("channelConnect.wizard.whatsapp.q2Yes")}
            noLabel={t("channelConnect.wizard.whatsapp.q2No")}
            groupLabel={t("channelConnect.wizard.whatsapp.q2")}
          />
          {waAnswers.metaAccess === false && (
            <Collapsible defaultOpen className="rounded-xl border border-border/50 bg-muted/50 p-3 text-sm">
              <CollapsibleTrigger className="font-medium text-foreground">
                {t("channelConnect.wizard.whatsapp.q2HelpTitle")}
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2 space-y-2 text-muted-foreground">
                <p>{t("channelConnect.wizard.whatsapp.q2Help")}</p>
                <HelpLink href={CHANNEL_CONNECT_HELP_LINKS.whatsappMetaAdmin} label={t("channelConnect.wizard.learnMore")} />
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      );
    }

    if (currentStep === "confirm_oauth") {
      const checklist = isInstagram
        ? [
            t("channelConnect.wizard.instagram.checkProfessional"),
            t("channelConnect.wizard.instagram.checkFacebookPage"),
            t("channelConnect.wizard.instagram.checkMessageAccess"),
          ]
        : isEmail
          ? [t("channelConnect.email.wizard.prereqsBody")]
          : [
              t("channelConnect.wizard.whatsapp.checkBusiness"),
              t("channelConnect.wizard.whatsapp.checkMetaAccess"),
            ];

      return (
        <div className="space-y-4">
          <ul className="space-y-2">
            {checklist.map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" strokeWidth={2} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {isEmail && studios.length > 1 && (
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">{t("channelConnect.email.wizard.defaultStudioLabel")}</Label>
              <Select value={resolvedEmailDefaultStudioId} onValueChange={setEmailDefaultStudioId}>
                <SelectTrigger className="h-10 rounded-xl">
                  <SelectValue placeholder={t("channelConnect.email.wizard.defaultStudioLabel")} />
                </SelectTrigger>
                <SelectContent>
                  {studios.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[12px] text-muted-foreground">{t("channelConnect.email.wizard.defaultStudioHint")}</p>
            </div>
          )}
          {connectError && (
            <div className="flex items-start gap-2 rounded-xl border border-destructive/30 bg-destructive/5 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <p className="text-sm text-destructive">{t("channelConnect.errors.connectFailed")}</p>
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const isConfirm = currentStep === "confirm_oauth";
  const emailConfirmDisabled = isEmail && isConfirm && studios.length > 1 && !resolvedEmailDefaultStudioId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn(settingsAddModalMaxWidthClassName, "gap-0 p-0")}>
        <DialogHeader className="space-y-3 border-b border-border/50 px-6 py-5">
          <div className={cn("mx-auto flex h-12 w-12 items-center justify-center rounded-2xl", iconWrap)}>
            <ChannelIcon className="h-6 w-6" />
          </div>
          <DialogTitle className="text-center">{title}</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5">{renderBody()}</div>

        <DialogFooter className="flex-row gap-2 border-t border-border/50 px-6 py-4 sm:justify-between">
          {stepIndex > 0 && !isConfirm ? (
            <Button type="button" variant="outline" onClick={goBack}>
              {t("channelConnect.wizard.back")}
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            {!isConfirm && (
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t("channelConnect.wizard.cancel")}
              </Button>
            )}
            {isConfirm ? (
              <Button
                type="button"
                variant="gradient"
                disabled={connecting || emailConfirmDisabled}
                onClick={() => void handleConnect()}
              >
                {connecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : connectError ? (
                  t("channelConnect.wizard.retry")
                ) : isInstagram ? (
                  t("channelConnect.wizard.instagram.confirm")
                ) : isEmail ? (
                  t("channelConnect.email.wizard.confirm")
                ) : (
                  t("channelConnect.wizard.whatsapp.confirm")
                )}
              </Button>
            ) : (
              <Button type="button" variant="gradient" disabled={!canContinue()} onClick={goNext}>
                {t("channelConnect.wizard.continue")}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
